// pages/people/staff/View.tsx
import DetailView from "@/components/DetailView";
import {
  useStaffByIdQuery,
  useDeleteStaffMutation,
  useStaffQuery,
} from "@/hooks/api/staff.queries";
import { useBatchesQuery } from "@/hooks/api/batch.queries";
import { Spinner, ErrorMessage } from "@/components";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { StaffType } from "@/services/staff.service";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "@/store";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import StaffDialog from "@/components/formDialog/StaffDialog";
import { CustomAlertDialog } from "@/components";
import { Trash, Users, GraduationCap } from "lucide-react";
import { isHodDesignation } from "@/utils/hod.utils";

// ─── Sub-components ────────────────────────────────────────────────────────────

const StaffProfileCard = ({ staff }: { staff: StaffType }) => {
  const initials = staff.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const designationLabel = staff.designation_name
    ? isHodDesignation(staff.designation_name)
      ? "HOD"
      : staff.designation_name
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")
    : null;

  return (
    <Card className="border-gray-100 shadow-sm">
      <CardContent className="pb-8 pt-8">
        <div className="flex flex-col gap-6 md:flex-row">
          {/* Avatar */}
          <Avatar className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-xl font-bold text-white">
            <AvatarFallback className="bg-blue-600 text-white">
              {initials}
            </AvatarFallback>
          </Avatar>

          {/* Info */}
          <div className="flex-1 space-y-3">
            {/* Name + Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-900">{staff.name}</h2>
              <Badge
                variant={staff.is_active ? "secondary" : "destructive"}
                className="rounded-full px-3 font-medium"
              >
                {staff.is_active ? "Active" : "Inactive"}
              </Badge>
              {designationLabel && (
                <Badge
                  variant="default"
                  className="rounded-full px-3 font-medium"
                >
                  {designationLabel}
                </Badge>
              )}
              {staff.role_name && (
                <Badge
                  variant="outline"
                  className="rounded-full px-3 font-medium"
                >
                  {staff.role_name}
                </Badge>
              )}
            </div>

            {/* Contact row */}
            <div className="grid grid-cols-1 gap-x-12 gap-y-2 text-sm text-gray-500 sm:grid-cols-2">
              <span className="break-all">📧 {staff.org_email}</span>
              <span>📞 {staff.phone}</span>
              {staff.personal_email &&
                staff.personal_email !== staff.org_email && (
                  <span className="break-all">
                    📧 <span className="text-xs text-gray-400">(personal)</span>{" "}
                    {staff.personal_email}
                  </span>
                )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StaffDetailsCard = ({ staff }: { staff: StaffType }) => {
  const rows: { label: string; value: string | number | null | undefined }[] = [
    { label: "Employee ID", value: staff.employee_id },
    { label: "Department", value: staff.department_name },
    { label: "Join Date", value: staff.join_date },
    { label: "Qualification", value: staff.qualification },
    {
      label: "Experience",
      value:
        staff.experience_years != null
          ? `${staff.experience_years} years`
          : null,
    },
    { label: "Last Login", value: staff.last_login_at ?? "Never" },
    {
      label: "Created",
      value: new Date(staff.created_at).toLocaleDateString(),
    },
  ].filter((r) => r.value != null && r.value !== "");

  return (
    <Card className="border-gray-100 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold">Details</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-12 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex flex-col gap-0.5">
              <dt className="text-xs font-medium text-gray-400">{label}</dt>
              <dd className="text-gray-700">{value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

const HodDashboardWidgets = ({ staff }: { staff: StaffType }) => {
  const { data: staffData, isLoading: isStaffLoading } = useStaffQuery({
    department_id: staff.department_id || undefined,
    limit: 50,
  });
  const { data: batchesData, isLoading: isBatchesLoading } = useBatchesQuery({
    dept_id: staff.department_id || undefined,
    active: true,
    limit: 50,
  });

  const departmentStaff = (staffData?.staff || []).filter(
    (s: StaffType) => s.id !== staff.id
  );
  const totalStaffCount = staffData?.metaData?.pagination?.total
    ? Math.max(0, staffData.metaData.pagination.total - 1)
    : 0;

  const activeBatches = batchesData?.batches || [];
  const totalBatchesCount = batchesData?.metaData?.pagination?.total || 0;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <Card className="flex h-full flex-col border-gray-100 bg-white shadow-sm">
        <CardHeader className="mb-4 flex flex-row items-center justify-between space-y-0 border-b border-gray-50 px-6 pb-4 pt-6">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-gray-700" />
            <CardTitle className="text-base font-semibold tracking-tight text-gray-900">
              Department Staff
            </CardTitle>
          </div>
          <Badge
            variant="secondary"
            className="border-none bg-gray-100 font-medium text-gray-600 hover:bg-gray-100"
          >
            {totalStaffCount} Members
          </Badge>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-3 px-6 pb-6 pt-0">
          {isStaffLoading ? (
            <div className="py-4 text-center text-sm text-gray-500">
              Loading staff...
            </div>
          ) : departmentStaff.length === 0 ? (
            <div className="rounded-xl bg-gray-50 py-8 text-center text-sm text-gray-500">
              No other staff members found.
            </div>
          ) : (
            departmentStaff.map((member: StaffType) => (
              <div
                key={member.id}
                className="flex items-center justify-between rounded-xl border border-transparent bg-gray-50 p-4 transition-colors hover:bg-gray-100/60"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-gray-900">
                    {member.name}
                  </span>
                  <span className="mt-0.5 text-xs text-gray-500">
                    {member.designation_name || "Staff"}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 border border-gray-200 bg-white/50 text-xs font-semibold text-gray-700 shadow-sm hover:bg-white"
                  asChild
                >
                  <Link to={`/people/staff/${member.id}`}>View</Link>
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card className="flex h-full flex-col border-gray-100 bg-white shadow-sm">
        <CardHeader className="mb-4 flex flex-row items-center justify-between space-y-0 border-b border-gray-50 px-6 pb-4 pt-6">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-gray-700" />
            <CardTitle className="text-base font-semibold tracking-tight text-gray-900">
              Active Batches
            </CardTitle>
          </div>
          <Badge
            variant="secondary"
            className="border-none bg-gray-100 font-medium text-gray-600 hover:bg-gray-100"
          >
            {totalBatchesCount} Batches
          </Badge>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-3 px-6 pb-6 pt-0">
          {isBatchesLoading ? (
            <div className="py-4 text-center text-sm text-gray-500">
              Loading batches...
            </div>
          ) : activeBatches.length === 0 ? (
            <div className="rounded-xl bg-gray-50 py-8 text-center text-sm text-gray-500">
              No active batches found.
            </div>
          ) : (
            activeBatches.map((batch) => (
              <div
                key={batch.id}
                className="flex items-center justify-between rounded-xl border border-transparent bg-gray-50 p-4 transition-colors hover:bg-gray-100/60"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-gray-900">
                    {batch.name}
                  </span>
                  <span className="mt-0.5 text-xs text-gray-500">
                    Sem {batch.current_semester}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 border border-gray-200 bg-white/50 text-xs font-semibold text-gray-700 shadow-sm hover:bg-white"
                  asChild
                >
                  <Link to={`/batches/${batch.id}`}>View</Link>
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────

const StaffView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: staff,
    isLoading,
    isError,
    refetch,
  } = useStaffByIdQuery(id ?? "");

  const user = useAuthStore((state) => state.user);
  const canUpdate = user?.permissions?.includes("staff.update");
  const canDelete = user?.permissions?.includes("staff.delete");

  const deleteMutation = useDeleteStaffMutation({});
  const deleteDialog = useAlertDialog();

  const staffDialog = useFormDialog<{
    mode: "create" | "edit";
    id: string;
  }>({ mode: "create", id: "" });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (isError || !staff) {
    return (
      <ErrorMessage
        message="Failed to load staff details."
        onRetry={() => refetch()}
      />
    );
  }

  const handleDelete = () => {
    if (!id) return;
    deleteMutation.mutate(id, {
      onSuccess: () => {
        // Return to the same designation-filtered list (HOD / Admin / Faculty),
        // not the bare list which would show every staff member.
        const target = staff.designation_id
          ? `/people/staff?designation_id=${staff.designation_id}&designation_name=${encodeURIComponent(
              staff.designation_name ?? ""
            )}`
          : "/people/staff";
        navigate(target);
      },
      onSettled: () => deleteDialog.close(),
    });
  };

  return (
    <DetailView
      title="Staff"
      backPath={-1}
      actions={
        <div className="mt-2 flex w-full flex-wrap items-center gap-2 sm:mt-0 sm:w-auto">
          {canUpdate && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 flex-1 text-xs sm:flex-none"
              onClick={() => staffDialog.open({ mode: "edit", id: staff.id })}
            >
              Edit Staff
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 flex-1 border-red-200 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 sm:flex-none"
              onClick={() => {
                deleteDialog.open({
                  action: "delete",
                  mantraId: staff.id,
                  mantraTitle: staff.name,
                  message: (
                    <span>
                      Are you sure you want to delete{" "}
                      <strong className="text-gray-800">{staff.name}</strong>?
                      This action cannot be undone.
                    </span>
                  ),
                });
              }}
            >
              <Trash size={14} className="mr-1.5" />
              Delete
            </Button>
          )}
        </div>
      }
    >
      <StaffProfileCard staff={staff} />
      <StaffDetailsCard staff={staff} />

      {isHodDesignation(staff.designation_name) && (
        <HodDashboardWidgets staff={staff} />
      )}

      <StaffDialog
        mode={staffDialog.state.mode as "create" | "edit"}
        isOpen={staffDialog.state.isOpen}
        onOpenChange={(open) => !open && staffDialog.close()}
        staffId={staffDialog.state.id}
        onCancel={staffDialog.close}
        filters={{}}
      />

      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </DetailView>
  );
};

export default StaffView;
